import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api } from '../services/api';
import type { ProjectType, ProjectBillingModel } from '../types';

interface CreateProjectModalProps {
  organizationId: string;
  onClose: () => void;
}

export default function CreateProjectModal({ organizationId, onClose }: CreateProjectModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState<ProjectType>('service');
  const [description, setDescription] = useState('');
  const [billingModel, setBillingModel] = useState<ProjectBillingModel | ''>('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [fixedPrice, setFixedPrice] = useState('');
  const [totalBudgetHours, setTotalBudgetHours] = useState('');
  const [renewalType, setRenewalType] = useState<'one_time' | 'recurring'>('one_time');
  const [renewalDay, setRenewalDay] = useState('');
  const [showHoursToCustomer, setShowHoursToCustomer] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deadlineWarningDays, setDeadlineWarningDays] = useState('7');

  const mutation = useMutation({
    mutationFn: () => api.createProject({
      organization_id: organizationId,
      name,
      type,
      description: description || undefined,
      billing_model: billingModel || undefined,
      hourly_rate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      fixed_price: fixedPrice ? parseFloat(fixedPrice) : undefined,
      total_budget_hours: totalBudgetHours ? parseFloat(totalBudgetHours) : undefined,
      renewal_type: renewalType,
      renewal_day: renewalDay ? parseInt(renewalDay) : undefined,
      show_hours_to_customer: showHoursToCustomer,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      deadline_warning_days: deadlineWarningDays ? parseInt(deadlineWarningDays) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      onClose();
    },
  });

  const isHourBank = (type as string) === 'hour_bank' || (billingModel as string) === 'hour_bank';
  const isFixedPrice = (billingModel as string) === 'fixed_price';

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('createProject.title')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('createProject.name')}</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={255} />
          </div>

          <div>
            <label className="label">{t('createProject.type')}</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { value: 'service' as const, label: t('createProject.typeService'), desc: t('createProject.typeServiceDesc') },
                { value: 'hour_bank' as const, label: t('createProject.typeHourBank'), desc: t('createProject.typeHourBankDesc') },
                { value: 'development' as const, label: t('createProject.typeDevelopment'), desc: t('createProject.typeDevelopmentDesc') },
              ] as const).map((opt) => (
                <button key={opt.value} type="button"
                  onClick={() => setType(opt.value)}
                  className={`p-3 rounded-xl border-2 text-right transition-colors ${
                    type === opt.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-surface-400/15 hover:border-surface-400/15'
                  }`}>
                  <p className="text-sm font-semibold text-surface-800">{opt.label}</p>
                  <p className="text-xs text-surface-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">{t('common.description')}</label>
            <textarea className="input min-h-[60px]" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('createProject.billingModel')}</label>
              <select className="input" value={billingModel} onChange={(e) => setBillingModel(e.target.value as ProjectBillingModel)}>
                <option value="">{t('createProject.billingDefault')}</option>
                <option value="hourly">{t('createProject.billingHourly')}</option>
                <option value="hour_bank">{t('createProject.billingHourBank')}</option>
                <option value="fixed_price">{t('createProject.billingFixed')}</option>
              </select>
            </div>
            <div>
              <label className="label">{t('createProject.hourlyRate')}</label>
              <input type="number" step="0.01" className="input" value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)} placeholder={t('createProject.hourlyRateDefault')} />
            </div>
          </div>

          {(isHourBank || type === 'hour_bank') && (
            <div className="p-3 bg-warning-50 rounded-xl border border-warning-200 space-y-3">
              <p className="text-xs font-medium text-warning-700">{t('createProject.hourBankSettings')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label text-xs">{t('createProject.totalBudgetHours')}</label>
                  <input type="number" step="0.5" className="input" value={totalBudgetHours}
                    onChange={(e) => setTotalBudgetHours(e.target.value)} />
                </div>
                <div>
                  <label className="label text-xs">{t('createProject.renewalType')}</label>
                  <select className="input" value={renewalType}
                    onChange={(e) => setRenewalType(e.target.value as 'one_time' | 'recurring')}>
                    <option value="one_time">{t('createProject.oneTime')}</option>
                    <option value="recurring">{t('createProject.recurring')}</option>
                  </select>
                </div>
              </div>
              {renewalType === 'recurring' && (
                <div>
                  <label className="label text-xs">{t('createProject.renewalDay')}</label>
                  <input type="number" min={1} max={28} className="input" value={renewalDay}
                    onChange={(e) => setRenewalDay(e.target.value)} />
                </div>
              )}
            </div>
          )}

          {isFixedPrice && (
            <div>
              <label className="label">{t('createProject.fixedPrice')}</label>
              <input type="number" step="0.01" className="input" value={fixedPrice}
                onChange={(e) => setFixedPrice(e.target.value)} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('createProject.startDate')}</label>
              <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('createProject.endDate')}</label>
              <input type="date" className="input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="label">{t('createProject.warningDays')}</label>
            <input type="number" min={1} max={30} className="input" value={deadlineWarningDays}
              onChange={(e) => setDeadlineWarningDays(e.target.value)} placeholder="7" />
            <p className="text-xs text-surface-400 mt-1">{t('createProject.warningDaysDesc')}</p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showHoursToCustomer}
              onChange={(e) => setShowHoursToCustomer(e.target.checked)} className="w-4 h-4 accent-primary-600" />
            <span className="text-sm text-surface-700">{t('createProject.showHoursToCustomer')}</span>
          </label>

          {mutation.error && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">
              {t('createProject.createError')}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
              {mutation.isPending ? t('common.creating') : t('createProject.createProject')}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
